import React, { useState, useEffect, useRef } from 'react';
import { GRADIENTS, COLORS } from './themeConstants';
import { useHistory } from 'react-router-dom';

const ModulesSession = () => {
	const [activeModule, setActiveModule] = useState(null);
	const [isVisible, setIsVisible] = useState(false);
	const sectionRef = useRef(null);
	const history = useHistory();

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIsVisible(entry.isIntersecting);
			},
			{ threshold: 0.1 },
		);

		if (sectionRef.current) {
			observer.observe(sectionRef.current);
		}

		return () => observer.disconnect();
	}, []);

	const modules = [
		{
			title: 'Academic',
			icon: '🎓',
			description: 'Necttos is designed to redefine education and collaboration by utilizing advanced technology to create a seamless and engaging learning experience.',
		},
		{
			title: 'Co-Curricular',
			icon: '🎭',
			description: 'We foster holistic student development by integrating extracurricular activities, leadership programs, and skill-building initiatives into the academic framework.',
		},
		{
			title: 'Compliance',
			icon: '📋',
			description: 'Our platform ensures institutions meet regulatory and accreditation requirements efficiently, automating compliance workflows and reporting.',
		},
		{
			title: 'Administration',
			icon: '⚙️',
			description: 'Our platform streamlines institutional operations, ensuring the efficient management of academic, financial, and infrastructural resources.',
		},
	];

	return (
		<section ref={sectionRef} id='highlights' className={`min-h-screen py-24 bg-gradient-to-br ${GRADIENTS.background.primary} relative overflow-hidden`}>
			{/* Background Animation */}
			<div className='absolute inset-0 opacity-20'>
				<div className={`absolute w-full h-full bg-gradient-to-br ${GRADIENTS.background.overlay} animate-pulse`} />
			</div>

			<div className='container mx-auto px-4 relative z-10'>
				{/* Header */}
				<div className={`text-center mb-16 transform transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
					<h1 className={`relative text-5xl md:text-6xl lg:text-7xl font-extrabold mb-8 bg-gradient-to-r ${GRADIENTS.text.accent} bg-clip-text text-transparent leading-none pb-4`}>Highlights</h1>
					<p className={`text-lg md:text-xl ${COLORS.text.secondary} max-w-3xl mx-auto`}>Comprehensive solutions designed to transform educational institutions</p>
				</div>

				{/* Modules Grid */}
				<div className='grid md:grid-cols-2 gap-8 max-w-6xl mx-auto'>
					{modules.map((module, index) => (
						<div
							key={index}
							className={`transform transition-all duration-700 delay-${index * 200} 
                                ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
							<div
								className={`p-8 rounded-2xl backdrop-blur-lg ${COLORS.background.glass} 
                                    border ${COLORS.border.glass} hover:scale-105 transform 
                                    transition-all duration-300 cursor-pointer
                                    ${activeModule === index ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}`}
								onClick={() => setActiveModule(activeModule === index ? null : index)}>
								<div className='text-4xl mb-4'>{module.icon}</div>
								<h3 className={`text-2xl font-bold mb-4 bg-gradient-to-r ${GRADIENTS.text.secondary} bg-clip-text text-transparent`}>{module.title}</h3>
								<p className={`${COLORS.text.secondary} leading-relaxed`}>{module.description}</p>
								<button
									onClick={() => history.push('/know-more')}
									className={`mt-6 px-6 py-2 rounded-full text-sm font-semibold
                                        bg-gradient-to-r ${GRADIENTS.button.primary} text-white 
                                        opacity-80 hover:opacity-100 transition-all duration-300`}>
									Learn More →
								</button>
							</div>
						</div>
					))}
				</div>

				{/* CTA Section */}
				<div className={`mt-16 text-center transform transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
					<button
						onClick={() => history.push('/know-more')}
						className={`px-8 py-3 rounded-full bg-gradient-to-r ${GRADIENTS.button.secondary} 
                        text-white font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300`}>
						Explore All Features
					</button>
				</div>
			</div>
		</section>
	);
};

export default ModulesSession;
